import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const RegistrationModal = ({ isOpen, onClose, eventTitle }) => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    groupSize: '1',
    emergencyContact: '',
    emergencyPhone: '',
    dietaryRestrictions: '',
    tshirtSize: '',
    agreedToTerms: false,
    agreedToWaiver: false
  });

  const [errors, setErrors] = useState({});

  const groupSizeOptions = [
    { value: '1', label: 'Just me' },
    { value: '2', label: '2 people' },
    { value: '3', label: '3 people' },
    { value: '4', label: '4 people' },
    { value: '5+', label: '5 or more people' }
  ];

  const tshirtSizeOptions = [
    { value: 'xs', label: 'Extra Small' },
    { value: 's', label: 'Small' },
    { value: 'm', label: 'Medium' },
    { value: 'l', label: 'Large' },
    { value: 'xl', label: 'Extra Large' },
    { value: 'xxl', label: '2X Large' }
  ];

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData?.fullName?.trim()) newErrors.fullName = 'Full name is required';
    if (!formData?.email?.trim()) newErrors.email = 'Email is required';
    if (!formData?.phone?.trim()) newErrors.phone = 'Phone number is required';
    if (!formData?.emergencyContact?.trim()) newErrors.emergencyContact = 'Emergency contact is required';
    if (!formData?.emergencyPhone?.trim()) newErrors.emergencyPhone = 'Emergency phone is required';
    if (!formData?.tshirtSize) newErrors.tshirtSize = 'T-shirt size is required';
    if (!formData?.agreedToTerms) newErrors.agreedToTerms = 'You must agree to terms';
    if (!formData?.agreedToWaiver) newErrors.agreedToWaiver = 'You must sign the waiver';

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (validateForm()) {
      console.log('Registration submitted:', formData);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-[var(--color-background)] rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-[var(--color-border)]">
          <div>
            <h2 className="text-2xl font-bold text-[var(--color-foreground)]">Register for Event</h2>
            <p className="text-sm text-[var(--color-muted-foreground)] mt-1">{eventTitle}</p>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-lg hover:bg-[var(--color-muted)] flex items-center justify-center transition-colors"
            aria-label="Close modal"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-6">
          <div className="space-y-4">
            <h3 className="font-semibold text-[var(--color-foreground)]">Personal Information</h3>
            
            <Input
              label="Full Name"
              type="text"
              placeholder="Enter your full name"
              value={formData?.fullName}
              onChange={(e) => handleChange('fullName', e?.target?.value)}
              error={errors?.fullName}
              required
            />

            <div className="grid sm:grid-cols-2 gap-4">
              <Input
                label="Email Address"
                type="email"
                placeholder="your.email@example.com"
                value={formData?.email}
                onChange={(e) => handleChange('email', e?.target?.value)}
                error={errors?.email}
                required
              />

              <Input
                label="Phone Number"
                type="tel"
                placeholder="(555) 123-4567"
                value={formData?.phone}
                onChange={(e) => handleChange('phone', e?.target?.value)}
                error={errors?.phone}
                required
              />
            </div>

            <Select
              label="Group Size"
              description="How many people will be attending?"
              options={groupSizeOptions}
              value={formData?.groupSize}
              onChange={(value) => handleChange('groupSize', value)}
            />
          </div>

          <div className="space-y-4 pt-4 border-t border-[var(--color-border)]">
            <h3 className="font-semibold text-[var(--color-foreground)]">Emergency Contact</h3>
            
            <div className="grid sm:grid-cols-2 gap-4">
              <Input
                label="Emergency Contact Name"
                type="text"
                placeholder="Contact name"
                value={formData?.emergencyContact}
                onChange={(e) => handleChange('emergencyContact', e?.target?.value)}
                error={errors?.emergencyContact}
                required
              />

              <Input
                label="Emergency Contact Phone"
                type="tel"
                placeholder="(555) 123-4567"
                value={formData?.emergencyPhone}
                onChange={(e) => handleChange('emergencyPhone', e?.target?.value)}
                error={errors?.emergencyPhone}
                required
              />
            </div>
          </div>

          <div className="space-y-4 pt-4 border-t border-[var(--color-border)]">
            <h3 className="font-semibold text-[var(--color-foreground)]">Additional Information</h3>
            
            <Input
              label="Dietary Restrictions"
              type="text"
              placeholder="Any allergies or dietary needs? (Optional)"
              value={formData?.dietaryRestrictions}
              onChange={(e) => handleChange('dietaryRestrictions', e?.target?.value)}
            />

            <Select
              label="T-Shirt Size"
              description="We'll provide event t-shirts for all volunteers"
              options={tshirtSizeOptions}
              value={formData?.tshirtSize}
              onChange={(value) => handleChange('tshirtSize', value)}
              error={errors?.tshirtSize}
              required
            />
          </div>

          <div className="space-y-4 pt-4 border-t border-[var(--color-border)]">
            <h3 className="font-semibold text-[var(--color-foreground)]">Terms & Conditions</h3>
            
            <Checkbox
              label="I agree to the terms and conditions"
              description="I understand the event requirements and commit to attending"
              checked={formData?.agreedToTerms}
              onChange={(e) => handleChange('agreedToTerms', e?.target?.checked)}
              error={errors?.agreedToTerms}
              required
            />

            <Checkbox
              label="I agree to the liability waiver"
              description="I acknowledge the risks and release the organization from liability"
              checked={formData?.agreedToWaiver}
              onChange={(e) => handleChange('agreedToWaiver', e?.target?.checked)}
              error={errors?.agreedToWaiver}
              required
            />
          </div>
        </form>

        <div className="flex gap-3 p-6 border-t border-[var(--color-border)]">
          <Button
            variant="outline"
            fullWidth
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            variant="default"
            fullWidth
            onClick={handleSubmit}
          >
            Complete Registration
          </Button>
        </div>
      </div>
    </div>
  );
};

export default RegistrationModal;